import { defineMetrics } from "../types.js"

export const changesMetrics = defineMetrics([
  {
    id: "strategy_changes",
    name: "Strategy Changes",
    description: "Number of strategy modifications.",
    logic:
      "Count changes made to Strategy records. Requires a change/version-history log, not just the current Strategy record",
    source: "auditTrail(module=CCP, action=Add Task Strategy | Modify Strategy)",
    domain: "changes",
    srvModule: "changes",
    priority: "must",
    valueType: "count",
    dims: [],
    rollup: "sum",
    defaultWidget: "bar",
    altWidgets: ["line", "stacked-bar", "stat-tile"],
    coveredByV1: false,
    caveat:
      "Counts strategy added and strategy modified. Approve Strategy is deliberately excluded: approving does not change the strategy content, and the dictionary asks for changes made to Strategy records.",
    xlsxRow: 66,
  },
  {
    id: "procedure_updates",
    name: "Procedure Updates",
    description: "Number of Procedure modifications.",
    logic: "Count updates made to Procedures. Requires a change/version-history log.",
    source: "none — the Task Procedures Update action is declared in srv but never written",
    domain: "changes",
    srvModule: "changes",
    priority: "must",
    valueType: "count",
    dims: [],
    rollup: "sum",
    defaultWidget: "bar",
    altWidgets: ["line", "stacked-bar", "stat-tile"],
    coveredByV1: false,
    caveat:
      "Reported unsupported. srv declares the audit action Task Procedures Update in its own vocabulary but no code path ever writes it, so matching on it would return 0 for ever without failing. Not aliased to Change Assessment, which fires on a different event (a change list gaining an action), because that would answer a different question under this name.",
    xlsxRow: 67,
  },
  {
    id: "document_list_changes",
    name: "Document List Changes",
    description: "Number of Document List modifications.",
    logic: "Count additions, removals, or edits to Document Lists. Requires a change/version-history log.",
    source: "auditTrail(module=CCP, action=Add | Remove | Task Documents Update)",
    domain: "changes",
    srvModule: "changes",
    priority: "must",
    valueType: "count",
    dims: [],
    rollup: "sum",
    defaultWidget: "bar",
    altWidgets: ["line", "stacked-bar", "stat-tile"],
    coveredByV1: false,
    caveat:
      "The dictionary's three verbs map one-to-one onto srv's three document-list actions. The DMS actions (Document Upload / Update / Delete) are excluded: those attach a file to a task rather than editing the task's own document list, and mixing them would let one upload read as one document-list change.",
    xlsxRow: 68,
  },
  {
    id: "impact_changes",
    name: "Impact Changes",
    description: "Number of changes made to Impact values.",
    logic: "Count modifications to the Impact field. Requires a change/version-history log.",
    source: "none — impact is only in audit field lists that no call site uses",
    domain: "changes",
    srvModule: "changes",
    priority: "must",
    valueType: "count",
    dims: [],
    rollup: "sum",
    defaultWidget: "bar",
    altWidgets: ["line", "stacked-bar", "stat-tile"],
    coveredByV1: false,
    caveat:
      "Reported unsupported. CCPTask.impact exists as a five-value enum, but the only audit field lists that include impact are never used by any call site, so no audit row records a change to it. The live Task list carries impactRequired, a boolean meaning whether an impact assessment is needed, which is a different question. Adding impact to the live list is a one-line srv change but alters what is recorded for every customer, so it is tracked as a product decision (T-24).",
    xlsxRow: 69,
  },
  {
    id: "action_required_changes",
    name: "Action Required Changes",
    description: "Number of changes made to Action Required values.",
    logic: "Count modifications to the Action Required field. Requires a change/version-history log.",
    source: "auditTrail(module=CCP) rows whose newValues include the actionRequired field",
    domain: "changes",
    srvModule: "changes",
    priority: "must",
    valueType: "count",
    dims: [],
    rollup: "sum",
    defaultWidget: "bar",
    altWidgets: ["line", "stacked-bar", "stat-tile"],
    coveredByV1: false,
    caveat:
      "Counted from audit rows whose newValues array contains the actionRequired field. srv only records a field in newValues when it actually changed, so membership already means modified; counting by action instead would count whole task updates.",
    xlsxRow: 70,
  },
])
